/**
 * MOJE OSTRVO — a member's own island.
 *
 * Everything else in this product is the curriculum: a path somebody else laid out, with a next step
 * and a reason for every tile. This is the one place that is nobody's curriculum. It has no nodes, no
 * denominator, no completion, nothing to finish and nothing to be behind on. That is not a missing
 * feature list, it is the whole design — GDD §2.2 cut nine cosmetic slots because nine things to
 * collect is nine things to understand, and the line `docs/IZGLED.md` drew for the wardrobe applies
 * here word for word: **if a screen would ever have to tell somebody they cannot have something, it
 * has become the shop that was cut.**
 *
 * Why this file is `core` and pure: three places have to agree about what a legal island is — the
 * builder that draws one, the endpoint that stores one, and the gate that decides whether a visitor
 * can be let in. `core/terrain.ts` exists for exactly the same reason and this module leans on it
 * rather than restating any of it. A drawing that validates in one place and strands somebody in
 * another is the one failure with no recovery path.
 *
 * The stored shape is deliberately the same vocabulary as an authored island's `svet` block —
 * `teren`, `dok`, `ukrasi` — so the renderer needs no second code path. What it does NOT have is
 * `mesta` (there is no curriculum here) or `likovi` (a character carries dialogue, dialogue is free
 * text, and free text is a moderation surface this product has never had and does not want).
 */

import { z } from 'zod';

import { PROP_IDS, type PropId } from './content';
import {
  Terrain,
  decodeTerrain,
  encodeTerrain,
  inBounds,
  isSolid,
  isStandable,
  reachableFrom,
  tileOf,
  type Grid,
} from './terrain';

/**
 * One size, forever.
 *
 * Thirty-two tiles square is about four screens of walking and comfortably more than anybody fills.
 * It is fixed rather than growable because a canvas that can grow is a canvas with a number attached
 * to how big it is, and the research on this is one-sided: small bounded spaces get decorated and
 * big ones get abandoned. Club Penguin igloos got furnished; MMO housing plots sat empty.
 */
export const DOM_SIZE = 32;

/** Decorations per island. Generous, and a ceiling that exists so it can be a fact and not a fee. */
export const DOM_MAX_PROPS = 64;

/**
 * The most an island may weigh, checked on the exact bytes about to be stored.
 *
 * A run-length terrain string for 32×32 is a few hundred bytes even when hand-painted, and sixty-four
 * decorations are about twenty each, so this is roughly eight times what a full island needs. Bounded
 * at design time on purpose: every game that discovered its ceiling during an incident had to impose
 * it retroactively, which is the version everybody resents.
 */
export const DOM_MAX_BYTES = 8_192;

/**
 * Guests at once.
 *
 * The room has no seat limit of its own, and on a curriculum island the cap is implicit — a few
 * hundred members spread over thirteen islands, only those who unlocked it. An island with an open
 * door and its code pasted into a Skool thread has no such bound, and forty bodies on a 32×32 canvas
 * is not the feature described.
 */
export const DOM_MAX_GUESTS = 6;

/** The smallest island that is an island rather than a rock. Below this the door will not open. */
export const DOM_MIN_LAND = 24;

/* ------------------------------------------------------------------ the shape */

/**
 * Where a decoration came from — and the only reason this field exists today.
 *
 * A currency for buying new decorations is planned. When it arrives, the honest way for it to arrive
 * is as a third *source*, not as a price on the things already here: `kupljeno` switches on, the
 * catalogue grows, and nothing anybody already had acquires a number. Modelling it as provenance now
 * costs one string and makes the retrofit — the thing that actually breaks trust, see RuneScape's
 * Squeal of Fortune — structurally unavailable.
 *
 * There is deliberately no `cena` field, and the palette shows only what a member can place. A grid
 * of priced things you do not own is the shop GDD §2.2 cut, and it is the single most resented
 * pattern in this entire genre.
 */
export const DOM_SOURCES = ['osnovno', 'kupljeno'] as const;
export type DomSource = (typeof DOM_SOURCES)[number];

const tile = z.object({
  x: z.number().int().min(0).max(DOM_SIZE - 1),
  y: z.number().int().min(0).max(DOM_SIZE - 1),
});

const propSchema = z.object({
  vrsta: z.enum(PROP_IDS),
  x: z.number().int().min(0).max(DOM_SIZE - 1),
  y: z.number().int().min(0).max(DOM_SIZE - 1),
  izvor: z.enum(DOM_SOURCES).default('osnovno'),
});

export const domSchema = z.object({
  /**
   * Stored even though it is fixed, because an island that outlives a decision to change the size is
   * an island whose terrain string no longer decodes — and `decodeTerrain` answering null means
   * "this member has no island", which would quietly hand somebody a fresh one.
   */
  sirina: z.literal(DOM_SIZE),
  visina: z.literal(DOM_SIZE),
  teren: z.string().min(1).max(DOM_MAX_BYTES),
  dok: tile,
  ukrasi: z.array(propSchema).max(DOM_MAX_PROPS).default([]),
});

export type RawDom = z.infer<typeof domSchema>;
export type DomProp = z.infer<typeof propSchema>;

/** Parse a stored island, or null. Null always means "fall back", never "throw". */
export function readDom(value: unknown): RawDom | null {
  const parsed = domSchema.safeParse(value);
  if (!parsed.success) return null;
  if (!decodeTerrain(parsed.data.teren, parsed.data.sirina, parsed.data.visina)) return null;
  return parsed.data;
}

export const gridOf = (dom: RawDom): Grid | null => {
  const tiles = decodeTerrain(dom.teren, dom.sirina, dom.visina);
  return tiles ? { width: dom.sirina, height: dom.visina, tiles } : null;
};

/* ------------------------------------------------------------------ the gate */

export interface DomProblem {
  /** Serbian, because the only person who ever reads one is the member who painted it. */
  message: string;
  code: 'dok' | 'malo' | 'odsečeno' | 'ukras';
}

/**
 * What is wrong with this island, in the order it should be said.
 *
 * **One at a time.** The caller shows the first and nothing else — this is GDD §2② („jedan ključ")
 * at island scale, and the alternative is a member being handed a list of complaints about a place
 * they made for fun.
 *
 * The check that matters is the last one. A visitor arrives at the dock, so every tile they could
 * want has to be reachable from it: an island with a walled-off pocket plus „dođi kod mene" is a
 * griefing tool, and it is exactly the failure `reachableFrom` was written to prevent one layer up.
 * Decorations are never solid — a rule inherited from the world builder rather than re-decided — so
 * no arrangement of them can wall anybody in, and only painted terrain can.
 */
export function domProblems(dom: RawDom): DomProblem[] {
  const problems: DomProblem[] = [];
  const grid = gridOf(dom);
  if (!grid) return [{ message: 'Ostrvo ne može da se pročita.', code: 'dok' }];

  if (!isStandable(tileOf(grid, dom.dok.x, dom.dok.y)))
    problems.push({ message: 'Na doku se ne može stajati — tu gost stiže.', code: 'dok' });

  const land: number[] = [];
  for (let y = 0; y < grid.height; y++)
    for (let x = 0; x < grid.width; x++)
      if (isStandable(tileOf(grid, x, y))) land.push(y * grid.width + x);

  if (land.length < DOM_MIN_LAND)
    problems.push({ message: 'Premalo kopna — ostalo je više mora nego ostrva.', code: 'malo' });

  if (problems.length === 0) {
    const visited = reachableFrom(grid, dom.dok);
    const cut = land.filter((index) => !visited[index]).length;
    if (cut > 0)
      problems.push({
        message: `Do ${cut === 1 ? 'jednog polja' : `${cut} polja`} se ne može doći od doka.`,
        code: 'odsečeno',
      });
  }

  for (const prop of dom.ukrasi)
    if (!inBounds(grid, prop.x, prop.y)) {
      problems.push({ message: 'Nešto stoji van ostrva.', code: 'ukras' });
      break;
    }

  return problems;
}

/* ------------------------------------------------------------------ the name */

/**
 * Two words, both chosen from a list.
 *
 * A member typing their own island name is the smallest possible moderation queue, and it is still a
 * moderation queue — `docs/UZIVO.md` §7 keeps this product at "no free text means nothing to
 * moderate" and the four fixed gestures are the precedent. A closed vocabulary is expressive enough
 * to feel chosen (there are several hundred combinations) and costs nobody an afternoon of reading
 * reports.
 *
 * Both lists are append-only: the stored value is a pair of indices, so reordering either one
 * silently renames every island that was ever named.
 */
export const DOM_WORDS_A = [
  'Tihi',
  'Mirni',
  'Sunčani',
  'Zeleni',
  'Daleki',
  'Mali',
  'Skriveni',
  'Stari',
  'Vetroviti',
  'Zlatni',
  'Plavi',
  'Topli',
] as const;

export const DOM_WORDS_B = [
  'zaliv',
  'greben',
  'rt',
  'sprud',
  'žal',
  'krug',
  'kutak',
  'prolaz',
  'vrh',
  'kamen',
  'gaj',
  'pristan',
] as const;

/** The name, or a plain fallback for an island nobody has named. */
export function domName(a: number | null, b: number | null): string {
  const first = DOM_WORDS_A[a ?? -1];
  const second = DOM_WORDS_B[b ?? -1];
  return first && second ? `${first} ${second}` : 'Ostrvo';
}

export const isNameIndex = (a: unknown, b: unknown): boolean =>
  typeof a === 'number' &&
  typeof b === 'number' &&
  Number.isInteger(a) &&
  Number.isInteger(b) &&
  a >= 0 &&
  b >= 0 &&
  a < DOM_WORDS_A.length &&
  b < DOM_WORDS_B.length;

/* ------------------------------------------------------------------ the first island */

/**
 * A small deterministic hash of the member id, so two people do not get the same island and one
 * person gets the same one twice.
 */
function seedOf(memberId: string): () => number {
  let hash = 2166136261;
  for (let index = 0; index < memberId.length; index++) {
    hash ^= memberId.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  let state = hash >>> 0 || 1;
  return () => {
    // xorshift32. Not for anything that matters, only for where the trees go.
    state ^= state << 13;
    state ^= state >>> 17;
    state ^= state << 5;
    state >>>= 0;
    return state / 4294967296;
  };
}

/**
 * The island somebody starts with.
 *
 * **Never an empty grid.** This is the single most documented way a decorating feature dies: a
 * player is handed a blank canvas and a full toolbox, flattens everything, and is stuck for months.
 * The Animal Crossing forums are full of it under titles like „i wanted terraforming til i realized
 * i am bad at it". The world builder learned the same lesson (`seedWorld`) and the comment there
 * says it plainly: seeding from something is the difference between a tool somebody uses and a tool
 * somebody opens once.
 *
 * So this is a finished small island: a beach all the way round, grass inside it, a few trees and
 * rocks that are already in nice places, and a dock on the south shore. Everything about it can be
 * painted over on the first tap.
 */
export function seedDom(memberId: string): RawDom {
  const random = seedOf(memberId);
  const size = DOM_SIZE;
  const tiles = new Uint8Array(size * size).fill(Terrain.Sea);

  const cx = size / 2;
  const cy = size / 2;
  const radiusX = 11 + Math.floor(random() * 2);
  const radiusY = 9 + Math.floor(random() * 2);
  // A little noise on the radius per angle, so the coast is a coast and not an ellipse.
  const wobble = Array.from({ length: 16 }, () => 0.86 + random() * 0.28);

  const shore = (x: number, y: number): number => {
    const angle = Math.atan2(y - cy, x - cx);
    const slot = Math.floor(((angle + Math.PI) / (Math.PI * 2)) * wobble.length) % wobble.length;
    const factor = wobble[slot] ?? 1;
    const nx = (x - cx) / (radiusX * factor);
    const ny = (y - cy) / (radiusY * factor);
    return nx * nx + ny * ny;
  };

  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const distance = shore(x, y);
      if (distance <= 0.62) tiles[y * size + x] = Terrain.Ground;
      else if (distance <= 1) tiles[y * size + x] = Terrain.Sand;
      else if (distance <= 1.35) tiles[y * size + x] = Terrain.Shallow;
    }
  }

  // A few trees and rocks, only well inside the grass so the beach stays walkable all the way round.
  const scatter = (what: Terrain, count: number) => {
    for (let tries = 0, placed = 0; tries < count * 20 && placed < count; tries++) {
      const x = Math.floor(random() * size);
      const y = Math.floor(random() * size);
      if (tiles[y * size + x] !== Terrain.Ground) continue;
      if (shore(x, y) > 0.42) continue;
      tiles[y * size + x] = what;
      placed++;
    }
  };
  scatter(Terrain.Tree, 7);
  scatter(Terrain.Rock, 3);
  scatter(Terrain.Flowers, 5);
  scatter(Terrain.TallGrass, 6);

  /**
   * The dock, on the south shore, with planks reaching out into the water.
   *
   * Placed by walking up from the bottom until the ground is standable rather than by arithmetic:
   * the coastline is noisy, and a dock computed from the radius lands in the sea about a third of
   * the time.
   */
  const dockX = Math.floor(cx);
  let dockY = size - 1;
  while (dockY > 0 && !isStandable((tiles[dockY * size + dockX] ?? Terrain.Sea) as Terrain)) dockY--;
  for (let y = dockY + 1; y < size && y <= dockY + 3; y++) tiles[y * size + dockX] = Terrain.Dock;
  tiles[dockY * size + dockX] = Terrain.Sand;

  /**
   * Open up anything the scatter sealed off.
   *
   * The trees and rocks are placed independently, so four of them can close a ring around a tile
   * without any one of them knowing. About one member in nine hundred was handed an island their own
   * door refused — „Do jednog polja se ne može doći od doka" on a brand-new island they had never
   * touched, with no way to discover that the answer was to paint over a tree. It was found by
   * fuzzing twenty-five thousand ids, which is the only way it was ever going to be found: six
   * sampled ids all pass.
   *
   * What has to be cleared is the RING, not the pocket — the trapped tile is already grass, and the
   * first attempt at this fix wrote grass over grass and changed nothing. So each pass turns the
   * solid neighbours of anything unreachable back into grass, which is a hole in the wall, and
   * repeats until the flood fill covers every standable tile. It converges in one or two passes
   * because each one strictly shrinks the sealed region; the bound is there so a future change to
   * the scatter cannot turn this into a hang.
   */
  const grid = { width: size, height: size, tiles };
  for (let pass = 0; pass < 8; pass++) {
    const reached = reachableFrom(grid, { x: dockX, y: dockY });
    let sealed = 0;
    for (let index = 0; index < tiles.length; index++) {
      if (reached[index] || !isStandable(tiles[index] as Terrain)) continue;
      sealed++;
      const x = index % size;
      const y = (index - x) / size;
      for (const [dx, dy] of [
        [0, -1],
        [0, 1],
        [-1, 0],
        [1, 0],
      ] as const) {
        const nx = x + dx;
        const ny = y + dy;
        if (nx < 0 || ny < 0 || nx >= size || ny >= size) continue;
        if (isSolid(tiles[ny * size + nx] as Terrain)) tiles[ny * size + nx] = Terrain.Ground;
      }
    }
    if (sealed === 0) break;
  }

  return {
    sirina: size,
    visina: size,
    teren: encodeTerrain(tiles, size, size),
    dok: { x: dockX, y: dockY },
    ukrasi: [],
  };
}

/* ------------------------------------------------------------------ the door */

/**
 * Who may come in.
 *
 * Three states and no friend graph, no request, no accept and no decline. `docs/UZIVO.md` §1 refused
 * an invitation round trip because it „only invents this product's first rejection", and that
 * promise is kept here: a code is something you hand out, not something anybody has to say no to.
 * Closing the door IS the revoke.
 */
export const DOM_DOORS = ['zatvoreno', 'kod', 'otvoreno'] as const;
export type DomDoor = (typeof DOM_DOORS)[number];

export const isDoor = (value: unknown): value is DomDoor =>
  typeof value === 'string' && (DOM_DOORS as readonly string[]).includes(value);

/**
 * The alphabet a door code is written in.
 *
 * Crockford's, minus the letters that are the digit next to them in a Skool message read on a phone.
 * Six characters is about a billion, which is the right amount for a key somebody pastes into a chat
 * rather than a secret anybody is attacking.
 */
export const DOM_CODE_ALPHABET = '23456789ABCDEFGHJKMNPQRSTVWXYZ';
export const DOM_CODE_LENGTH = 6;

/** Uppercase, separators dropped, and the two characters people always mistype folded in. */
export function normaliseDomCode(input: string): string {
  return input
    .toUpperCase()
    .replace(/[^0-9A-Z]/g, '')
    .replace(/O/g, '0')
    .replace(/[IL]/g, '1')
    .slice(0, DOM_CODE_LENGTH);
}

export const looksLikeDomCode = (input: string): boolean =>
  normaliseDomCode(input).length === DOM_CODE_LENGTH;

/** Grouped in threes, the way `authCode.ts` shows the login code. */
export const formatDomCode = (code: string): string =>
  code.length === DOM_CODE_LENGTH ? `${code.slice(0, 3)} ${code.slice(3)}` : code;

/**
 * Which prop kinds a member may place.
 *
 * Everything, today, and that is the point — there is no locked row and nothing greyed out. It is a
 * function rather than a constant so that a future catalogue can grow without anybody being shown
 * what they have not got: new kinds are appended, and a member who owns none of them sees the list
 * they had yesterday rather than a wall of things with prices on.
 */
export const domPropKinds = (): readonly PropId[] => PROP_IDS;
